import { useCallback, useMemo } from 'react';

import { StoreItemInventoryWrapper } from '#components/molecules/StoreItemInventoryWrapper/StoreItemInventoryWrapper.js';
import { StoreItem, StoreItemWithOptionalInventorySummary } from '@givebacks/api/store';
import { useForm, useFormyValidator } from '@givebacks/formy';
import { usePeerCreditsForm } from 'core/Components';
import { centsToDollar } from 'core/Utils';
import { RichTextDisplay } from 'rich-text';
import {
  AdditionalFieldsFormsGuard,
  Alert,
  Box,
  CurrencyInput,
  Divider,
  QuantitySelector,
  Stack,
  Text,
} from 'ui';

import { CheckoutButtons } from '../../molecules/Cart/buttons/CheckoutButtons/CheckoutButtons';
import {
  ADDITIONAL_FIELDS_FORM_ID,
  StoreItemDetailsFormType,
  StoreItemOrderForms,
} from './StoreItemForm.types';

type StoreItemFormFooterProps = {
  storeItem: StoreItem;
  storeId: string;
  onSuccess?: () => void;
};

const StoreItemFormFooter = ({ storeItem, storeId, onSuccess }: StoreItemFormFooterProps) => {
  const { useGetForms, useScrollToError } = useFormyValidator<StoreItemOrderForms>();
  const forms = useGetForms();
  const { credit_to, credit_write_in } = usePeerCreditsForm();
  const { scrollToError } = useScrollToError({
    scrollParams: {
      duration: 300,
      offset: 84,
    },
  });

  const storeItemId = useMemo(() => storeItem.uuid, [storeItem.uuid]);

  const productCartItems = {
    price: forms.store_item_details?.values?.price ?? storeItem?.price,
    quantity: forms.store_item_details?.values?.quantity,
    options: forms[ADDITIONAL_FIELDS_FORM_ID]?.getTransformedValues().options.asObject,
    store_item_id: storeItem.uuid,
    credit_to: credit_to,
    credit_write_in: credit_write_in,
  };

  return (
    <StoreItemInventoryWrapper
      storeItem={storeItem}
      storeId={storeId}
      additionalFieldsFormId={ADDITIONAL_FIELDS_FORM_ID}
    >
      {({ isOutOfStock }) => (
        <CheckoutButtons
          storeId={storeId}
          storeItemId={storeItemId}
          cartItems={[productCartItems]}
          onCartCreate={() => onSuccess?.()}
          addToCartProps={{
            onAddToCartClick: (args, handleCartUpdate) => {
              handleCartUpdate();
              requestAnimationFrame(() => {
                scrollToError({ scrollTo: 'input', startingFrom: 'top' });
              });
            },
          }}
          buyNowProps={{
            onBuyNowClick: (handleCartUpdate) => {
              handleCartUpdate();
              requestAnimationFrame(() => {
                scrollToError({ scrollTo: 'input', startingFrom: 'top' });
              });
            },
          }}
          validateOnClick
          addToCartVisible={!isOutOfStock}
          buyNowVisible={!isOutOfStock}
        />
      )}
    </StoreItemInventoryWrapper>
  );
};

type StoreItemFormProps = {
  withFooter?: boolean;
  storeItem: StoreItemWithOptionalInventorySummary;
  storeId: string;
};

const StoreItemForm = ({ storeItem, storeId, withFooter = true }: StoreItemFormProps) => {
  const form = useForm<StoreItemDetailsFormType>(
    {
      initialValues: {
        price: !storeItem.variable_price ? storeItem.price : null,
        quantity: 1,
      },
      validate: {
        price: (value) => {
          if (!storeItem.minimum_price) return null;
          if (!value || (value && value < storeItem.minimum_price)) {
            return `The price must be above ${centsToDollar(storeItem.minimum_price)}`;
          }
        },
      },
      validateInputOnBlur: true,
    },
    {
      id: 'store_item_details',
    }
  );

  const additionalFields = useMemo(() => storeItem.options ?? [], [storeItem.options]);

  const isVariablePrice = useMemo(() => storeItem.variable_price, [storeItem.variable_price]);

  const onVariantChange = useCallback(() => form.setFieldValue('quantity', 1), [form]);

  return (
    <Stack gap="lg">
      <Box ref={form.formRef}>
        <StoreItemInventoryWrapper
          storeItem={storeItem}
          storeId={storeId}
          additionalFieldsFormId={ADDITIONAL_FIELDS_FORM_ID}
          onVariantChange={onVariantChange}
        >
          {({ isOutOfStock, isLowInventory, inventoryCount }) => (
            <>
              {isOutOfStock && <Alert type="error" title="Sold Out" />}

              {isVariablePrice && !isOutOfStock && (
                <CurrencyInput
                  label="Enter your price"
                  {...form.getInputProps('price')}
                  description={
                    storeItem.minimum_price
                      ? `${centsToDollar(storeItem.minimum_price)} minimum`
                      : undefined
                  }
                  required={storeItem.variable_price && !storeItem.price}
                />
              )}

              {!!storeItem.content.short_description && (
                <Text type="body">{storeItem.content.short_description}</Text>
              )}
              {!!storeItem.content.long_description && (
                <RichTextDisplay html={storeItem.content.long_description} />
              )}

              {isLowInventory && (
                <Alert type="warning" variant="light">
                  Only {inventoryCount} left in stock — order soon.
                </Alert>
              )}

              {isVariablePrice == false && !isOutOfStock && (
                <QuantitySelector
                  value={form.values.quantity}
                  size="lg"
                  label="Select quantity"
                  minValue={1}
                  onChange={(quantity) => form.setFieldValue('quantity', quantity)}
                />
              )}
            </>
          )}
        </StoreItemInventoryWrapper>
      </Box>
      <AdditionalFieldsFormsGuard options={additionalFields} formId={ADDITIONAL_FIELDS_FORM_ID} />

      {withFooter && (
        <>
          <Divider />
          <StoreItemFormFooter storeId={storeId} storeItem={storeItem} />
        </>
      )}
    </Stack>
  );
};

export { StoreItemForm, StoreItemFormFooter };
